/**
 * Gyro's first-party usage record, the client half: one module shared by the web widget, the Office pane (the same
 * widget, layout 'pane') and Gyro Admin. Spec: the usage analytics contract (2026-10-29), sections 1, 4, 6 and 7.
 *
 * What it records: which screens open and which features run, how often and for how long, per account, in batches of
 * events the service stores under the signed-in caller it already resolves. Identity never travels in a batch, and a
 * param is only ever one of eight fixed keys with a scalar value (strings cut at 40 characters): no message, document
 * or free text can reach the record from here.
 *
 * Rules, all from the contract: a session is new on cold start, on user change and after 30 minutes away; events are
 * numbered 1, 2, 3... per session (the service de-duplicates on session_id and seq); a screen's engagement is credited
 * when it is left, on the screen_view event while it is still queued, else as a trailing screen_engagement event; the
 * queue holds 2,000 events at most (the oldest go first); a flush sends batches of 500, every 60 s or as soon as 100
 * are queued, one flush at a time; a refused batch is dropped on 400, 413 and 422 and kept (with a backoff of 1 to 5
 * minutes) on 401, 404, 429, 5xx and a network failure; the queue is persisted per signed-in user ({owner, events}
 * under localStorage key gyro.usage.queue) and restored on cold start; sign-out flushes then drops it; a user change
 * drops the other user's queue and starts a new session. Nothing here throws into UI code: every public function is
 * wrapped and a failure goes to the debug log at warn.
 */
/** The params allowlist (contract section 6): at most these eight keys, scalar values, strings of 40 characters or fewer. */
export const USAGE_PARAM_KEYS = ['app', 'tool', 'model', 'skill', 'tab', 'kind', 'result', 'count'];
export const USAGE_SURFACES = ['web', 'desktop', 'office', 'admin'];
export const USAGE_QUEUE_KEY = 'gyro.usage.queue';
export const USAGE_QUEUE_CAP = 2000;
export const USAGE_BATCH_SIZE = 500;
export const USAGE_SOFT_FLUSH_AT = 100;
export const USAGE_FLUSH_INTERVAL_MS = 60_000;
export const USAGE_SESSION_GAP_MS = 30 * 60_000;
export const USAGE_BODY_LIMIT_BYTES = 256 * 1024;
export const USAGE_BACKOFF_MAX = 5;
export const USAGE_PARAM_STRING_MAX = 40;
/** A screen or action name: section 1's shape. */
export const USAGE_NAME_PATTERN = /^[A-Za-z0-9_.:-]{1,64}$/;
/** Engagement credited to one screen visit is capped at the session gap: longer means the person was away, not reading. */
const ENGAGEMENT_CAP_MS = USAGE_SESSION_GAP_MS;
const DEFAULT_ENVIRONMENT = { surface: 'web', app_version: 'unknown', os_version: 'unknown', device_class: 'desktop' };
/** The allowlist applied: unknown keys, non-scalar values, empty strings and NaN go; strings are cut at 40 characters. */
export function cleanUsageParams(input) {
    const clean = {};
    if (!input || typeof input !== 'object')
        return clean;
    for (const key of USAGE_PARAM_KEYS) {
        if (!Object.hasOwn(input, key))
            continue;
        const value = input[key];
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed)
                clean[key] = trimmed.length > USAGE_PARAM_STRING_MAX ? trimmed.slice(0, USAGE_PARAM_STRING_MAX) : trimmed;
        }
        else if (typeof value === 'number') {
            if (Number.isFinite(value))
                clean[key] = value;
        }
        else if (typeof value === 'boolean') {
            clean[key] = value;
        }
    }
    return clean;
}
/** A session id: the runtime's UUID where it has one. */
export function usageId() {
    const c = globalThis.crypto;
    if (typeof c?.randomUUID === 'function')
        return c.randomUUID();
    return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e12).toString(36)}`;
}
/** Whether the sink's answer settles a batch: sent (2xx), dropped (400, 413, 422) or kept for a retry (everything else). */
export function usageBatchOutcome(status) {
    if (status >= 200 && status < 300)
        return 'sent';
    if (status === 400 || status === 413 || status === 422)
        return 'dropped';
    return 'kept';
}
/** The wait before the next try after `failures` failed flushes in a row: 1 to 5 minutes. */
export function usageBackoffMs(failures) {
    return USAGE_FLUSH_INTERVAL_MS * Math.min(Math.max(failures, 1), USAGE_BACKOFF_MAX);
}
function isUsageEvent(value) {
    if (!value || typeof value !== 'object')
        return false;
    const e = value;
    return typeof e.session_id === 'string' && typeof e.seq === 'number' && typeof e.ts === 'string' && typeof e.kind === 'string'
        && typeof e.name === 'string' && USAGE_NAME_PATTERN.test(e.name) && (e.params === undefined || (typeof e.params === 'object' && e.params !== null));
}
/** The persisted queue, when the stored text is one and belongs to `owner`; anything else (junk, another user's) is nothing. */
export function readPersistedUsageQueue(text, owner) {
    if (!text)
        return null;
    try {
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== 'object' || parsed.owner !== owner || !Array.isArray(parsed.events))
            return null;
        return parsed.events.filter(isUsageEvent).map((event) => ({ ...event, params: cleanUsageParams(event.params) }));
    }
    catch {
        return null;
    }
}
function pageStorage() {
    try {
        const storage = globalThis.localStorage;
        return storage && typeof storage.getItem === 'function' ? storage : null;
    }
    catch {
        return null;
    }
}
export function createUsageCapture(options = {}) {
    const now = options.now ?? (() => Date.now());
    const newId = options.newId ?? usageId;
    const timers = options.timers ?? {
        set: (fn, ms) => {
            const handle = setTimeout(fn, ms);
            // Under Node (the tests, a server-side render) a pending flush timer must not keep the process alive.
            handle.unref?.();
            return handle;
        },
        clear: (handle) => clearTimeout(handle),
    };
    const warn = options.warn ?? ((message, detail) => (detail === undefined ? console.warn(message) : console.warn(message, detail)));
    const storage = options.storage === undefined ? pageStorage() : options.storage;
    let sink = options.sink ?? null;
    const environment = { ...DEFAULT_ENVIRONMENT };
    /** The fields something has set: a host's value (`configure`) replaces any; a default (`configureDefaults`) fills only an unset one. */
    const assigned = new Set();
    let owner = null;
    let sessionId = null;
    let seq = 0;
    let lastActivity = 0;
    let queue = [];
    let current = null;
    let failures = 0;
    let backoffUntil = 0;
    let timer = null;
    let inFlight = null;
    /** The events of the batch being sent: already serialized, so a credit can no longer land on them. */
    let inFlightEvents = new Set();
    let overflowWarned = false;
    let hooksInstalled = false;
    /** Every public function runs through this: a failure is logged at warn and never reaches the caller. */
    function guard(name, fn) {
        return (...args) => {
            try {
                fn(...args);
            }
            catch (error) {
                warn(`[gyro usage] ${name} failed`, error);
            }
        };
    }
    function guardAsync(name, fn) {
        return async (...args) => {
            try {
                await fn(...args);
            }
            catch (error) {
                warn(`[gyro usage] ${name} failed`, error);
            }
        };
    }
    function applyEnvironment(patch, explicit) {
        const takes = (key) => explicit || !assigned.has(key);
        if (patch.surface !== undefined && USAGE_SURFACES.includes(patch.surface) && takes('surface')) {
            environment.surface = patch.surface;
            assigned.add('surface');
        }
        for (const key of ['app_version', 'os_version']) {
            const value = patch[key];
            if (typeof value === 'string' && value.trim() && takes(key)) {
                environment[key] = value.trim().slice(0, 64);
                assigned.add(key);
            }
        }
        if (patch.device_class !== undefined && ['desktop', 'phone', 'tablet'].includes(patch.device_class) && takes('device_class')) {
            environment.device_class = patch.device_class;
            assigned.add('device_class');
        }
    }
    if (options.environment)
        applyEnvironment(options.environment, true);
    function persist() {
        if (!storage || !owner)
            return;
        try {
            if (queue.length === 0)
                storage.removeItem(USAGE_QUEUE_KEY);
            else
                storage.setItem(USAGE_QUEUE_KEY, JSON.stringify({ owner, events: queue }));
        }
        catch (error) {
            warn('[gyro usage] the queue could not be persisted', error);
        }
    }
    function dropPersisted() {
        if (!storage)
            return;
        try {
            storage.removeItem(USAGE_QUEUE_KEY);
        }
        catch {
            /* storage unavailable: nothing was persisted there */
        }
    }
    function enqueue(event) {
        queue.push(event);
        if (queue.length > USAGE_QUEUE_CAP) {
            queue.splice(0, queue.length - USAGE_QUEUE_CAP);
            if (!overflowWarned) {
                overflowWarned = true;
                warn(`[gyro usage] the queue is full (${USAGE_QUEUE_CAP}); the oldest events are dropped until a flush succeeds`);
            }
        }
    }
    function pushEvent(kind, name, params, extra = {}) {
        if (!sessionId)
            throw new Error('no session');
        seq += 1;
        const event = { session_id: sessionId, seq, ts: new Date(now()).toISOString(), kind, name, params, ...extra };
        enqueue(event);
        return event;
    }
    /** Credits the screen on view with the time since it was entered, then forgets the entry time (a later credit starts at `at`). */
    function creditCurrent(at) {
        const screen = current;
        if (!screen || screen.frozen)
            return;
        const ms = Math.max(0, Math.min(at - screen.enteredAt, ENGAGEMENT_CAP_MS));
        screen.enteredAt = at;
        if (ms === 0)
            return;
        const queued = queue.find((event) => event.kind === 'screen_view' && event.session_id === screen.sessionId && event.seq === screen.seq && !inFlightEvents.has(event));
        if (queued)
            queued.engagement_ms = (queued.engagement_ms ?? 0) + ms;
        else if (sessionId)
            pushEvent('screen_engagement', screen.name, {}, { engagement_ms: ms });
    }
    function startSession(at) {
        sessionId = newId();
        seq = 0;
        lastActivity = at;
        pushEvent('session_start', 'session', {});
    }
    /** The session an event joins: the current one, or a new one on cold start and after 30 minutes away. */
    function ensureSession(at) {
        if (sessionId && at - lastActivity <= USAGE_SESSION_GAP_MS) {
            lastActivity = at;
            return;
        }
        // An expired session's last screen is credited up to the idle cut-off (30 minutes after the last activity): the
        // stretch beyond it was time away, not time on the screen. A screen frozen in the background was credited already.
        const expired = sessionId !== null;
        if (expired)
            creditCurrent(Math.min(at, lastActivity + USAGE_SESSION_GAP_MS));
        startSession(at);
        if (expired && current) {
            // The screen still on view is viewed again in the new session.
            const view = pushEvent('screen_view', current.name, current.params);
            current = { ...current, enteredAt: at, sessionId: view.session_id, seq: view.seq, frozen: false };
        }
    }
    function afterEnqueue() {
        persist();
        if (queue.length >= USAGE_SOFT_FLUSH_AT)
            void flush();
        else
            arm(USAGE_FLUSH_INTERVAL_MS);
    }
    function arm(ms) {
        if (timer !== null || queue.length === 0 || !sink)
            return;
        const wait = Math.max(ms, backoffUntil - now());
        timer = timers.set(() => {
            timer = null;
            void flush();
        }, wait);
    }
    function disarm() {
        if (timer === null)
            return;
        timers.clear(timer);
        timer = null;
    }
    /** A batch whose JSON stays under the body limit: halved until it fits (an event alone that does not fit is dropped). */
    function nextBatch() {
        let count = Math.min(USAGE_BATCH_SIZE, queue.length);
        for (;;) {
            const events = queue.slice(0, count);
            const body = { ...environment, events };
            if (JSON.stringify(body).length <= USAGE_BODY_LIMIT_BYTES)
                return { events, body };
            if (count === 1) {
                warn(`[gyro usage] an event larger than the body limit was dropped: ${events[0]?.name ?? ''}`);
                queue.shift();
                if (queue.length === 0)
                    return null;
                count = Math.min(USAGE_BATCH_SIZE, queue.length);
                continue;
            }
            count = Math.ceil(count / 2);
        }
    }
    async function runFlush(keepalive, force) {
        const send = sink;
        if (!send)
            return;
        while (queue.length > 0) {
            if (!force && backoffUntil > now())
                break;
            const batch = nextBatch();
            if (!batch)
                break;
            let status = 0;
            inFlightEvents = new Set(batch.events);
            try {
                status = await send(batch.body, { keepalive });
            }
            catch (error) {
                warn('[gyro usage] a batch could not be sent', error);
            }
            finally {
                inFlightEvents = new Set();
            }
            const outcome = usageBatchOutcome(typeof status === 'number' ? status : 0);
            if (outcome === 'kept') {
                failures = Math.min(failures + 1, USAGE_BACKOFF_MAX);
                backoffUntil = now() + usageBackoffMs(failures);
                warn(`[gyro usage] batch of ${batch.events.length} kept after status ${status}; next try in ${Math.round(usageBackoffMs(failures) / 1000)} s`);
                break;
            }
            force = false; // one forced batch; after it the backoff rules apply again
            // Sent or dropped: the batch leaves the queue either way (the queue may have grown behind it meanwhile).
            queue = queue.filter((event) => !batch.events.includes(event));
            failures = 0;
            backoffUntil = 0;
            overflowWarned = false;
            if (outcome === 'dropped')
                warn(`[gyro usage] batch of ${batch.events.length} refused with status ${status} and dropped`);
            persist();
        }
        if (queue.length > 0)
            arm(USAGE_FLUSH_INTERVAL_MS);
    }
    function flush(options = {}) {
        if (inFlight)
            return inFlight;
        if (!sink || queue.length === 0)
            return Promise.resolve();
        if (!options.force && backoffUntil > now()) {
            arm(0);
            return Promise.resolve();
        }
        disarm();
        inFlight = runFlush(options.keepalive === true, options.force === true)
            .catch((error) => warn('[gyro usage] flush failed', error))
            .finally(() => {
            inFlight = null;
        });
        return inFlight;
    }
    function dropAll() {
        disarm();
        queue = [];
        current = null;
        sessionId = null;
        seq = 0;
        failures = 0;
        backoffUntil = 0;
        dropPersisted();
    }
    const capture = {
        configure: guard('configure', (patch) => applyEnvironment(patch, true)),
        configureDefaults: guard('configureDefaults', (patch) => applyEnvironment(patch, false)),
        installSink: guard('installSink', (next) => {
            sink = next;
            if (sink && queue.length > 0)
                arm(USAGE_FLUSH_INTERVAL_MS);
        }),
        setUser: guard('setUser', (next) => {
            const key = typeof next === 'string' ? next.trim() : '';
            if (!key) {
                if (owner !== null)
                    void capture.signOut();
                return;
            }
            if (owner === key)
                return;
            if (owner !== null) {
                // Another account: that person's events never travel under this one.
                dropAll();
            }
            owner = key;
            const restored = storage ? readPersistedUsageQueue(safeRead(), key) : null;
            if (restored === null)
                dropPersisted();
            else if (restored.length > 0) {
                queue = [...restored, ...queue];
                if (queue.length > USAGE_QUEUE_CAP)
                    queue.splice(0, queue.length - USAGE_QUEUE_CAP);
            }
            backoffUntil = 0;
            failures = 0;
            if (queue.length > 0)
                afterEnqueue();
        }),
        screen: guard('screen', (name, params) => {
            if (typeof name !== 'string' || !USAGE_NAME_PATTERN.test(name)) {
                warn(`[gyro usage] screen name refused: ${String(name).slice(0, 80)}`);
                return;
            }
            const clean = cleanUsageParams(params);
            const at = now();
            ensureSession(at);
            if (current && current.name === name && current.sessionId === sessionId && JSON.stringify(current.params) === JSON.stringify(clean))
                return;
            const previous = current?.name;
            creditCurrent(at);
            const view = pushEvent('screen_view', name, clean, previous === undefined ? {} : { previous_screen: previous });
            current = { name, params: clean, enteredAt: at, sessionId: view.session_id, seq: view.seq, frozen: false };
            afterEnqueue();
        }),
        leaveScreen: guard('leaveScreen', () => {
            if (!current)
                return;
            creditCurrent(now());
            current = null;
            afterEnqueue();
        }),
        feature: guard('feature', (name, params) => {
            if (typeof name !== 'string' || !USAGE_NAME_PATTERN.test(name)) {
                warn(`[gyro usage] action name refused: ${String(name).slice(0, 80)}`);
                return;
            }
            const clean = cleanUsageParams(params);
            ensureSession(now());
            pushEvent('feature', name, clean);
            afterEnqueue();
        }),
        background: guard('background', () => {
            creditCurrent(now());
            if (current)
                current.frozen = true;
            persist();
            if (queue.length > 0)
                void flush({ keepalive: true });
        }),
        foreground: guard('foreground', () => {
            const at = now();
            if (current) {
                current.frozen = false;
                current.enteredAt = at;
            }
            if (sessionId && at - lastActivity > USAGE_SESSION_GAP_MS) {
                ensureSession(at);
                afterEnqueue();
            }
        }),
        flush: guardAsync('flush', (options) => flush(options)),
        signOut: guardAsync('signOut', async () => {
            creditCurrent(now());
            // One immediate try, backoff or not: what is queued is this account's last chance to travel before it is dropped.
            await flush({ force: true });
            owner = null;
            dropAll();
        }),
        installBrowserHooks: guard('installBrowserHooks', (target) => {
            const win = target === undefined ? globalThis : target;
            if (hooksInstalled || !win || typeof win.addEventListener !== 'function' || !win.document)
                return;
            hooksInstalled = true;
            win.document.addEventListener('visibilitychange', () => {
                if (win.document.visibilityState === 'hidden')
                    capture.background();
                else
                    capture.foreground();
            });
            win.addEventListener('pagehide', () => capture.background());
        }),
        snapshot: () => ({
            owner, sessionId, seq, queued: queue.length, currentScreen: current?.name ?? null, failures, flushing: inFlight !== null,
            environment: { ...environment }, hasSink: sink !== null,
        }),
    };
    function safeRead() {
        try {
            return storage?.getItem(USAGE_QUEUE_KEY) ?? null;
        }
        catch {
            return null;
        }
    }
    return capture;
}
let shared = null;
/** The one capture the widget, the Office pane and Gyro Admin share in a page (telemetry.ts's `track` feeds it). */
export function usageCapture() {
    if (!shared)
        shared = createUsageCapture();
    return shared;
}
//# sourceMappingURL=featureEvents.js.map